import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { useGoogleMaps } from './GoogleMapsLoader';
import './MapViewer.css';

const MapViewer = ({
    direcciones = [],
    onMarkerClick,
    selectedDireccionIndex = null,
    onLocationSelect
}) => {
    const { isLoaded } = useGoogleMaps();
    const [mapCenter, setMapCenter] = useState({
        lat: 21.8853,
        lng: -102.2916
    });
    const [zoom, setZoom] = useState(12);

    const mapContainerStyle = {
        width: '100%',
        height: '100%'
    };

    // Estilos personalizados del mapa - ocultar lugares/POI
    const mapStyles = [
        {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.business',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.park',
            elementType: 'labels.text',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.attraction',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.government',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.medical',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.place_of_worship',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.school',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'poi.sports_complex',
            stylers: [{ visibility: 'off' }]
        },
        {
            featureType: 'transit',
            elementType: 'labels.icon',
            stylers: [{ visibility: 'off' }]
        }
    ];

    // Direcciones válidas con coordenadas
    const validDirecciones = direcciones.filter(dir =>
        dir.latitud && dir.longitud
    );

    useEffect(() => {
        if (validDirecciones.length > 0) {
            // Centrar el mapa en la primera dirección principal o la primera disponible
            const principalDir = validDirecciones.find(dir => dir.es_principal);
            const targetDir = principalDir || validDirecciones[0];

            setMapCenter({
                lat: targetDir.latitud,
                lng: targetDir.longitud
            });

            // Ajustar zoom según cantidad de marcadores
            if (validDirecciones.length === 1) {
                setZoom(15);
            } else {
                setZoom(13);
            }
        }
    }, [validDirecciones.length]);

    if (!isLoaded) {
        return (
            <div className="map-viewer-loading">
                <MapPin size={48} />
                <p>Cargando mapa...</p>
            </div>
        );
    }

    const handleMapClick = async (e) => {
        if (selectedDireccionIndex !== null && onLocationSelect) {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();

            // Intentar obtener la dirección mediante geocodificación inversa
            try {
                const geocoder = new window.google.maps.Geocoder();
                const response = await geocoder.geocode({ location: { lat, lng } });

                if (response.results && response.results[0]) {
                    const address = response.results[0].formatted_address;
                    onLocationSelect(selectedDireccionIndex, { lat, lng, address });
                } else {
                    onLocationSelect(selectedDireccionIndex, { lat, lng });
                }
            } catch (error) {
                console.error('Error en geocodificación inversa:', error);
                onLocationSelect(selectedDireccionIndex, { lat, lng });
            }
        }
    };

    const handleMarkerDragEnd = async (index, e) => {
        if (onLocationSelect) {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();

            // Intentar obtener la dirección mediante geocodificación inversa
            try {
                const geocoder = new window.google.maps.Geocoder();
                const response = await geocoder.geocode({ location: { lat, lng } });

                if (response.results && response.results[0]) {
                    const address = response.results[0].formatted_address;
                    onLocationSelect(index, { lat, lng, address });
                } else {
                    onLocationSelect(index, { lat, lng });
                }
            } catch (error) {
                console.error('Error en geocodificación inversa:', error);
                onLocationSelect(index, { lat, lng });
            }
        }
    };

    const isSelectionMode = selectedDireccionIndex !== null;

    return (
        <div className="map-viewer">
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={zoom}
                onClick={handleMapClick}
                options={{
                    streetViewControl: false,
                    mapTypeControl: true,
                    fullscreenControl: true,
                    zoomControl: true,
                    cursor: isSelectionMode ? 'crosshair' : 'default',
                    styles: mapStyles
                }}
            >
                {validDirecciones.map((dir, index) => {
                    const direccionOriginalIndex = direcciones.findIndex(d => d === dir);
                    const isSelected = direccionOriginalIndex === selectedDireccionIndex;

                    return (
                        <Marker
                            key={index}
                            position={{
                                lat: dir.latitud,
                                lng: dir.longitud
                            }}
                            label={{
                                text: dir.es_principal ? '★' : `${index + 1}`,
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}
                            title={dir.direccion}
                            onClick={() => onMarkerClick && onMarkerClick(direccionOriginalIndex)}
                            draggable={isSelected}
                            onDragEnd={(e) => handleMarkerDragEnd(direccionOriginalIndex, e)}
                            icon={dir.es_principal ? {
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: isSelected ? 12 : 10,
                                fillColor: isSelected ? '#10b981' : '#3b82f6',
                                fillOpacity: 1,
                                strokeColor: 'white',
                                strokeWeight: isSelected ? 3 : 2
                            } : isSelected ? {
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 12,
                                fillColor: '#10b981',
                                fillOpacity: 1,
                                strokeColor: 'white',
                                strokeWeight: 3
                            } : undefined}
                        />
                    );
                })}
            </GoogleMap>

            {validDirecciones.length === 0 && !isSelectionMode && (
                <div className="map-viewer-overlay">
                    <MapPin size={48} />
                    <h3>Sin ubicaciones</h3>
                    <p>Agrega direcciones con coordenadas para verlas en el mapa</p>
                </div>
            )}

            {isSelectionMode && (
                <div className="map-selection-indicator">
                    <MapPin size={18} />
                    <span>Haz clic en el mapa para seleccionar ubicación y obtener dirección</span>
                </div>
            )}

            {validDirecciones.length > 0 && (
                <div className="map-viewer-legend">
                    <div className="legend-item">
                        <span className="legend-marker principal">★</span>
                        <span>Dirección principal</span>
                    </div>
                    {validDirecciones.length > 1 && (
                        <div className="legend-item">
                            <span className="legend-marker">{validDirecciones.length}</span>
                            <span>{validDirecciones.length} ubicaciones</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MapViewer;
